if (!customElements.get('book-bundle')) {
  const PICKER_SELECTOR = 'variant-picker, swatches-variant-picker-component';
  const BUNDLE_BUTTON_TEXT = 'Add bundle to cart';

  customElements.define('book-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.active = null;
      this.saved = null;
      this.onBundleChange = (event) => {
        const input = event.target instanceof Element ? event.target.closest('input[data-bundle-variant-id]') : null;
        if (input && input.checked) this.activate(input);
      };
      this.onSubmit = (event) => this.handleSubmit(event);
      this.onPickerInteraction = (event) => this.handlePickerInteraction(event);
      this.addEventListener('change', this.onBundleChange);
      document.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('click', this.onPickerInteraction, true);
      document.addEventListener('change', this.onPickerInteraction, true);
      requestAnimationFrame(() => {
        const restored = this.querySelector('input[data-bundle-variant-id]:checked');
        if (restored && !this.active) this.activate(restored);
      });
    }

    disconnectedCallback() {
      if (!this.bound) return;
      this.bound = false;
      this.removeEventListener('change', this.onBundleChange);
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('click', this.onPickerInteraction, true);
      document.removeEventListener('change', this.onPickerInteraction, true);
      if (this.active) this.deactivate();
    }

    get scope() {
      return this.closest('.shopify-section') || this.parentElement || document;
    }

    isMainInstance(element) {
      return element && !element.closest('dialog, product-card, .quick-add-modal');
    }

    getProductForm() {
      const productId = this.dataset.productId;
      const components = [...this.scope.querySelectorAll('product-form-component')];
      const match = components.find((component) => this.isMainInstance(component) && (!productId || component.dataset.productId === productId));
      return match ? match.querySelector('form[data-type="add-to-cart-form"]') : null;
    }

    getPicker() {
      const productId = this.dataset.productId;
      const pickers = [...this.scope.querySelectorAll(PICKER_SELECTOR)].filter((picker) => this.isMainInstance(picker));
      return pickers.find((picker) => picker.dataset.productId === productId) || (pickers.length === 1 ? pickers[0] : null);
    }

    getLabelNode(button) {
      const nodes = [...button.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '');
      return nodes[nodes.length - 1] || null;
    }

    activate(input) {
      if (!this.saved) this.saved = {};
      this.active = input;
      const form = this.getProductForm();
      const picker = this.getPicker();
      if (picker) picker.classList.add('book-bundle-deselected');

      if (form) {
        form.querySelectorAll('.accelerated-checkout-block').forEach((wrapper) => wrapper.classList.add('book-bundle-hidden'));
        const button = form.querySelector('[ref="addToCartButton"]');
        if (button) {
          const labelNode = this.getLabelNode(button);
          if (labelNode && !this.saved.labelNode) {
            this.saved.labelNode = labelNode;
            this.saved.labelText = labelNode.textContent;
          }
          if (labelNode) labelNode.textContent = input.dataset.bundlePrice ? BUNDLE_BUTTON_TEXT + ' \u00b7 ' + input.dataset.bundlePrice : BUNDLE_BUTTON_TEXT;
          if (!('disabled' in this.saved)) this.saved.disabled = button.disabled;
          this.saved.button = button;
          button.disabled = false;
        }
      }

      const sticky = this.scope.querySelector('sticky-add-to-cart');
      if (sticky) {
        const variantTitle = sticky.querySelector('.sticky-add-to-cart__variant');
        if (variantTitle) {
          if (!this.saved.variantTitle) {
            this.saved.variantTitle = variantTitle;
            this.saved.variantTitleText = variantTitle.textContent;
          }
          variantTitle.textContent = input.dataset.bundleLabel || '';
        }
        sticky.querySelectorAll('.sticky-add-to-cart__price').forEach((price) => price.classList.add('book-bundle-hidden'));
      }
    }

    deactivate() {
      this.active = null;
      this.querySelectorAll('input[data-bundle-variant-id]').forEach((input) => { input.checked = false; });
      const status = this.querySelector('[role="status"]');
      if (status) status.textContent = '';
      this.scope.querySelectorAll('.book-bundle-deselected').forEach((element) => element.classList.remove('book-bundle-deselected'));
      this.scope.querySelectorAll('.book-bundle-hidden').forEach((element) => element.classList.remove('book-bundle-hidden'));
      const saved = this.saved || {};
      if (saved.labelNode && saved.labelNode.isConnected) saved.labelNode.textContent = saved.labelText;
      if (saved.button && saved.button.isConnected && 'disabled' in saved) saved.button.disabled = saved.disabled;
      if (saved.variantTitle && saved.variantTitle.isConnected) saved.variantTitle.textContent = saved.variantTitleText;
      this.saved = null;
    }

    handlePickerInteraction(event) {
      if (!this.active) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const picker = target.closest(PICKER_SELECTOR);
      if (!picker || picker !== this.getPicker()) return;
      if (event.type === 'click' && !target.closest('label, input, select, option')) return;
      this.deactivate();
    }

    handleSubmit(event) {
      if (!this.active) return;
      const form = this.getProductForm();
      if (!form || event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addBundle(form);
    }

    async addBundle(form) {
      if (this.busy) return;
      const status = this.querySelector('[role="status"]');
      const button = form.querySelector('[ref="addToCartButton"]');
      const resetPuppet = () => { if (button) button.dataset.puppet = 'false'; };
      if (this.dataset.enabled !== 'true') {
        status.textContent = 'Preview only. No items were added. Bundle checkout will be available once the bundle is available.';
        resetPuppet();
        return;
      }
      const selected = this.active;
      const id = selected ? selected.dataset.bundleVariantId : '';
      if (!/^\d+$/.test(id || '')) {
        status.textContent = 'This bundle is unavailable. Please refresh the page.';
        resetPuppet();
        return;
      }
      const quantity = Math.max(1, parseInt(new FormData(form).get('quantity'), 10) || 1);
      this.busy = true;
      if (button) button.disabled = true;
      status.textContent = 'Adding both editions…';
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items: [{id, quantity}]})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'The bundle could not be added.');
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        status.textContent = 'Please check your cart before trying again. ' + (error.message || 'Unable to confirm the bundle.');
        if (button) button.disabled = false;
        resetPuppet();
        this.busy = false;
      }
    }
  });
}
